import { HUMAN_STRUCTURE, PHYSICAL_BREAKPOINT as PB } from "@/content/research";
import { PageIntro } from "@/components/site/PageIntro";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { ActionAnchor, Container, Eyebrow, Mono, SectionTitle, TextLink } from "@/components/site/primitives";
import { FormationExplorer } from "@/components/explorers/FormationExplorer";
import { ProductDiscovery } from "@/components/explorers/ProductDiscovery";

const HumanStructure = () => (
  <section id="human-structure" className="border-b border-o-line" data-testid="human-structure">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-5">
        <Eyebrow index="01">Shared intellectual foundation</Eyebrow>
        <SectionTitle className="mt-5">Human Structure</SectionTitle>
        <p className="mt-6 text-lg leading-relaxed">{HUMAN_STRUCTURE.lead}</p>
        <p className="mt-4 leading-relaxed text-o-dim">{HUMAN_STRUCTURE.body}</p>
        <TextLink to="/ordo" testid="human-structure-ordo" className="mt-6">General Ordo · the other shared foundation</TextLink>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <div className="grid grid-cols-2 gap-px border border-o-line bg-o-line sm:grid-cols-3">
          {HUMAN_STRUCTURE.elements.map((e, i) => (
            <div key={e} className="bg-o-surface p-5">
              <Mono accent>0{i + 1}</Mono>
              <p className="mt-6 font-display text-lg font-bold">{e}</p>
            </div>
          ))}
        </div>
        <div className="border-x border-b border-o-line bg-o-text p-5 text-o-bg">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] opacity-70">Arranged around</span>
          <p className="mt-1 font-display text-2xl font-bold">Human purposes</p>
        </div>
        <div className="mt-4 border border-dashed border-o-strong p-5">
          <Mono>Reserved for Phase 3</Mono>
          <p className="mt-2 text-sm text-o-dim">Extended explanation of Human Structure and its research.</p>
        </div>
      </div>
    </Container>
  </section>
);

const Block = ({ id, index, eyebrow, title, text, children }) => (
  <section id={id} className="border-b border-o-line" data-testid={`research-${id}`}>
    <Container className="py-16 lg:py-24">
      <div className="mb-10 grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow index={index}>{eyebrow}</Eyebrow>
          <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">{title}</SectionTitle>
        </div>
        <p className="text-o-dim lg:col-span-4 lg:col-start-9">{text}</p>
      </div>
      {children}
    </Container>
  </section>
);

const PhysicalBreakpoint = () => (
  <section id="physical-breakpoint" className="border-b border-o-line" data-testid="physical-breakpoint">
    <Container className="py-16 lg:py-20">
      <div className="grid gap-8 border border-o-line bg-o-surface p-5 sm:p-8 md:grid-cols-12 md:items-center">
        <Placeholder label="Physical Breakpoint product imagery" note="Supporting example" ratio="4 / 3" className="md:col-span-4" testid="placeholder-physical-breakpoint" />
        <div className="md:col-span-7 md:col-start-6">
          <Eyebrow>Supporting example · product formation</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold sm:text-3xl">{PB.title}</h2>
          <p className="mt-3 leading-relaxed">{PB.text}</p>
          <p className="mt-2 text-sm text-o-dim">{PB.note}</p>
          <TextLink to="/enterprise/breakpoint" testid="physical-breakpoint-software" className="mt-5">The Breakpoint software</TextLink>
        </div>
      </div>
    </Container>
  </section>
);

export default function ResearchPage() {
  return (
    <>
      <PageIntro routeKey="research" group="research" index="RP" eyebrow="Research & Products · Company-originated" title="Research & Products"
        lede="Research & Products begins with independent research into human needs and possibilities. It develops mechanisms and originates technologies and products around particular human activities."
        aside={<Placeholder label="Research and product formation illustration" note="From human activity to product" ratio="4 / 3" testid="placeholder-research" />}>
        <div className="mt-10 flex flex-wrap gap-3">
          <ActionAnchor href="#formation" testid="research-formation-btn">How products are formed</ActionAnchor>
          <ActionAnchor href="#products" variant="line" testid="research-products-btn">See the products</ActionAnchor>
        </div>
      </PageIntro>
      <HumanStructure />
      <Block id="formation" index="02" eyebrow="Product formation" title="Research → Formation → Prototype → Product → Learning"
        text="The important relationship: Ordo Intelligence begins with a human activity and develops the appropriate technological responsibility around it.">
        <FormationExplorer />
      </Block>
      <Block id="products" index="03" eyebrow="One method, distinct products" title="The same methods produce very different products."
        text="Select a product to see the human activity it begins from and the responsibility formed around it.">
        <ProductDiscovery />
      </Block>
      <PhysicalBreakpoint />
      <EngageBand path="research" from="/research" title="Collaborate on research or products."
        text="For researchers, product collaborators, institutions, and strategic supporters. During Phase 1 this inquiry is an inactive preview." />
    </>
  );
}
